import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { writeActionFor } from '@rbac/writeAction';
import DataTable from '@/components/DataTable';
import NewMockEnvironmentDialog from '@/components/mockapi/NewMockEnvironmentDialog';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Mock API" list — tenant-defined mock environments (services/mockapi/). Each one
 * stands in for a third-party API that is slow, metered, or does not exist yet, and is
 * served from an unguessable URL that anything can call.
 *
 * Mirrors WorkflowsPage: the same list → detail shape, because a mock environment is
 * the same kind of object — a user-authored definition with a detail editor behind it.
 */
const MODE_TONE = { mock: 'secondary', proxy: 'info', record: 'warning' };

function ModeBadge({ mode }) {
  return <Badge variant={MODE_TONE[mode] || 'secondary'} size="sm">{mode || 'mock'}</Badge>;
}

/** The active scenario is the single most consequential setting on the row — it decides
 *  what every route answers — so it is a column, not something behind a click. */
function ScenarioCell({ scenario }) {
  const isDefault = !scenario || scenario === 'default';
  return isDefault
    ? <span className="text-xs text-secondary-500">default</span>
    : <Badge variant="warning" size="sm">{scenario}</Badge>;
}

const COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    render: (e) => (
      <div>
        <div className="font-medium text-primary-600">{e.name}</div>
        <div className="text-secondary-500 text-xs max-w-xs truncate" title={e.description || e.key}>{e.description || e.key}</div>
      </div>
    ),
  },
  { key: 'is_active', label: 'Status', searchable: false, render: (e) => <Badge variant={e.is_active ? 'success' : 'secondary'} size="sm">{e.is_active ? 'active' : 'inactive'}</Badge> },
  { key: 'mode', label: 'Mode', searchable: false, render: (e) => <ModeBadge mode={e.mode} /> },
  { key: 'active_scenario', label: 'Scenario', searchable: false, render: (e) => <ScenarioCell scenario={e.active_scenario} /> },
  { key: 'route_count', label: 'Routes', searchable: false, render: (e) => e.route_count || 0 },
  { key: 'request_count', label: 'Requests', searchable: false, render: (e) => e.request_count || 0 },
  { key: 'last_request_at', label: 'Last called', searchable: false, render: (e) => (e.last_request_at ? new Date(e.last_request_at).toLocaleString() : 'never') },
];

export default function MockApiPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('mockEnvironments')
      .then((res) => setEnvironments((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Mock API"
        subtitle={(
          <>
            {environments.length} environment{environments.length === 1 ? '' : 's'} — stand-ins for third-party APIs, so agents and workflows can be built before the real integration exists.
          </>
        )}
        actions={(
          <Can module="mockapi" action={writeActionFor('mockapi')}>
            <Button size="sm" onClick={() => setCreating(true)}>New mock API</Button>
          </Can>
        )}
      />

      <DataTable
        data={environments}
        columns={COLUMNS}
        loading={loading}
        error={err}
        emptyText="No mock environments yet."
        search={{ placeholder: 'Filter mocks…', keys: ['name', 'key', 'description'] }}
        onRowClick={(e) => nav(`/app-mock-environment/${e.id}`)}
        onReload={reload}
      />

      {creating && (
        <NewMockEnvironmentDialog
          onClose={() => setCreating(false)}
          onCreated={(newId) => nav(`/app-mock-environment/${newId}`)}
        />
      )}
    </PageShell>
  );
}
